// Fiche d'une tournée : points ordonnés sur la carte et en liste, pointage de chaque point (heure
// relevée au moment du pointage, observation), clôture réalisée, partielle ou non réalisée avec
// motif, photos ; enregistrée sur l'appareil (opération realisation_tournee du contrat
// schemaRealisationTournee : points collectés, pointage résumé dans les observations, début et fin
// pris aux heures de pointage) puis synchronisée.
import type { EtatRealisation as EtatRealisationTournee } from '@cc/contrats';
import { Bouton, Champ, EtatVide, Formulaire, LienBouton, Squelette, ZoneTexte, type ErreurChamp } from '@cc/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { messageErreur } from '../../api/erreurs';
import { chargeRealisation, type PointagePoint as PointCollecteRealise } from '../../terrain/charges';
import { JOURS, LIBELLES_REALISATION, LIBELLES_TYPE_POINT, dateHeure, dateLongue, etatLocalObjet, etatLocalOperation } from '../../terrain/etat-local';
import { uuidV7 } from '../../terrain/identifiants';
import { synchroniseurPartage } from '../../terrain/synchronisation';
import { useOperationsLocales, useTourneeLocale } from '../../terrain/useTerrain';
import { CarteTerrain } from './CarteTerrain';
import { Definitions, EnTeteTerrain, EtatLocal, Ligne, Liste, Section, TexteEtat } from './commun';
import { SaisiePhotos, versPhotosAEnregistrer, type PhotoSaisie } from './SaisiePhotos';
import styles from './terrain.module.css';

export function PageTournee() {
  const { id } = useParams();
  const tournee = useTourneeLocale(id);
  const operations = useOperationsLocales(id);
  const [pointage, setPointage] = useState<Record<string, PointCollecteRealise>>({});
  const [etat, setEtat] = useState<EtatRealisationTournee | ''>('');
  const [motif, setMotif] = useState('');
  const [photos, setPhotos] = useState<PhotoSaisie[]>([]);
  const [erreurs, setErreurs] = useState<ErreurChamp[]>([]);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (tournee.chargement) return <Squelette lignes={6} libelle="Lecture de la tournée sur l'appareil" />;
  const t = tournee.donnees;
  if (!t) {
    return (
      <>
        <EnTeteTerrain titre="Tournée introuvable" retour="/terrain" />
        <EtatVide titre="Cette tournée n'est pas enregistrée sur cet appareil" niveau={2} action={<LienBouton href="/terrain">Retour à mon travail</LienBouton>} />
      </>
    );
  }
  const ops = operations.donnees ?? [];
  const realisations = ops.filter((o) => o.type === 'realisation_tournee');
  const points = [...t.points].sort((a, b) => a.ordre - b.ordre);
  const collectes = points.filter((p) => pointage[p.id]?.collecte).length;
  const etatDeduit: EtatRealisationTournee = collectes === points.length ? 'realisee' : collectes === 0 ? 'non_realisee' : 'partielle';
  const etatChoisi = etat || etatDeduit;

  const pointer = (pointId: string, collecte: boolean) => setPointage((p) => ({ ...p, [pointId]: { point_id: pointId, collecte, heure: collecte ? new Date().toISOString() : null, observation: p[pointId]?.observation ?? null } }));
  const observer = (pointId: string, observation: string) => setPointage((p) => ({ ...p, [pointId]: { point_id: pointId, collecte: p[pointId]?.collecte ?? false, heure: p[pointId]?.heure ?? null, observation: observation || null } }));

  const cloturer = async () => {
    const fautes: ErreurChamp[] = [];
    if (etatChoisi !== 'realisee' && motif.trim().length < 3) fautes.push({ champ: 'tournee-motif', message: 'Indiquez le motif d\'une tournée partielle ou non réalisée (trois caractères au moins)' });
    if (etatChoisi === 'partielle' && collectes === 0) fautes.push({ champ: 'tournee-etat', message: 'Une tournée réalisée en partie compte au moins un point collecté' });
    if (etatChoisi === 'realisee' && collectes !== points.length) fautes.push({ champ: 'tournee-etat', message: 'Tous les points doivent être pointés pour une tournée réalisée' });
    setErreurs(fautes);
    if (fautes.length) return;
    setEnCours(true);
    setMessage(null);
    try {
      const charge = chargeRealisation(t, pointage, etatChoisi, motif);
      await synchroniseurPartage().enfiler(
        { id_client: uuidV7(), type: 'realisation_tournee', objet_type: 'tournee', objet_id: t.id, version_attendue: t.version, enregistre_le: new Date().toISOString(), charge },
        { titre: `Réalisation de tournée (${LIBELLES_REALISATION[etatChoisi].toLowerCase()})`, objet_libelle: `${t.code} · ${t.nom}`, photos: versPhotosAEnregistrer(photos, 'realisation_tournee') },
      );
      setMessage(`Tournée ${LIBELLES_REALISATION[etatChoisi].toLowerCase()} enregistrée sur cet appareil : ${collectes} point(s) collecté(s) sur ${points.length}.`);
      setPhotos([]);
    } catch (e) {
      setMessage(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const etatLocal = etatLocalObjet(ops, t.id);
  return (
    <>
      <EnTeteTerrain
        titre={t.nom}
        surTitre={t.code}
        retour="/terrain"
        libelleRetour="Mon travail"
        sousTitre={
          <>
            {JOURS[t.jour_semaine] ?? ''} {t.heure} · {t.secteur.nom} · {dateLongue(t.date)}
            {etatLocal ? (
              <>
                {' · '}
                <EtatLocal etat={etatLocal} />
              </>
            ) : null}
          </>
        }
      />
      <Definitions elements={[{ terme: 'Véhicule', valeur: t.vehicule ?? 'Non renseigné' }, { terme: 'Points de collecte', valeur: String(points.length) }, { terme: 'Réalisation connue du serveur', valeur: t.realisation ? `${LIBELLES_REALISATION[t.realisation.etat]} le ${dateLongue(t.realisation.date)}` : 'aucune' }]} />
      <CarteTerrain titre="Carte de la tournée" points={points.filter((p) => p.position).map((p) => ({ id: p.id, numero: p.ordre, libelle: `${p.nom} (${LIBELLES_TYPE_POINT[p.type]})`, position: p.position as NonNullable<typeof p.position> }))} suivre />
      {realisations.length ? (
        <Section titre="Réalisations enregistrées">
          <Liste libelle="Réalisations">
            {realisations.map((o) => {
              const e = etatLocalOperation(o);
              return <Ligne key={o.id_client} href={e === 'conflit' ? '/terrain/conflits' : undefined} titre={o.titre} sousTitre={`Enregistrée le ${dateHeure(o.enregistre_le)}`} droite={e ? <EtatLocal etat={e} /> : <TexteEtat libelle="Rejetée" ton="attention" />} />;
            })}
          </Liste>
        </Section>
      ) : null}
      <Section titre="Pointage des points" droite={<span className={styles.aide}>{collectes} sur {points.length}</span>}>
        <Formulaire
          libelle="Réalisation de la tournée"
          onSoumettre={() => void cloturer()}
          erreurs={erreurs}
          actions={
            <Bouton type="submit" chargement={enCours} libelleChargement="Enregistrement sur l'appareil en cours">
              Clôturer la tournée
            </Bouton>
          }
        >
          {message ? (
            <p role="status" className={styles.aide}>
              {message}
            </p>
          ) : null}
          <ul className={styles.liste} aria-label="Points de la tournée">
            {points.map((p) => (
              <li key={p.id} className={styles.pointage}>
                <input type="checkbox" id={`point-${p.id}`} checked={pointage[p.id]?.collecte ?? false} onChange={(e) => pointer(p.id, e.target.checked)} aria-label={`Point ${p.ordre} collecté : ${p.nom}`} />
                <label htmlFor={`point-${p.id}`} className={styles.ligneTexte}>
                  <span className={styles.ligneTitre}>
                    <span className={styles.numero}>{String(p.ordre).padStart(2, '0')}</span> {p.nom}
                  </span>
                  <span className={styles.ligneSous}>
                    {LIBELLES_TYPE_POINT[p.type]} · {p.code}
                    {pointage[p.id]?.heure ? ` · pointé à ${dateHeure(pointage[p.id]?.heure).slice(-5)}` : ''}
                  </span>
                </label>
                <Champ id={`observation-${p.id}`} libelle="Observation" value={pointage[p.id]?.observation ?? ''} onChange={(e) => observer(p.id, e.target.value)} facultatif />
              </li>
            ))}
          </ul>
          <fieldset className={styles.pointControle} id="tournee-etat">
            <legend className={styles.aide}>Résultat de la tournée (déduit du pointage : {LIBELLES_REALISATION[etatDeduit].toLowerCase()})</legend>
            <div className={styles.choix}>
              {(['realisee', 'partielle', 'non_realisee'] as const).map((e) => (
                <label key={e}>
                  <input type="radio" name="tournee-etat" value={e} checked={etatChoisi === e} onChange={() => setEtat(e)} />
                  {LIBELLES_REALISATION[e]}
                </label>
              ))}
            </div>
          </fieldset>
          {etatChoisi !== 'realisee' ? <ZoneTexte id="tournee-motif" libelle="Motif" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} erreur={erreurs.find((f) => f.champ === 'tournee-motif')?.message} required aide="Véhicule en panne, accès bloqué, point inaccessible..." /> : null}
          <SaisiePhotos id="tournee-photos" photos={photos} onChange={setPhotos} max={4} />
        </Formulaire>
      </Section>
    </>
  );
}
